import React, { useState, useEffect } from "react";
import { FaFilePdf, FaArrowRight, FaHandPointUp, FaFire } from "react-icons/fa";
import { GrLocation } from "react-icons/gr";
import { PiBedLight } from "react-icons/pi";
import { FaProcedures } from "react-icons/fa";
import { MdOutlinePhone } from "react-icons/md";
import ApartmentBeforeContent from "./ApartmentBeforeContent";
import ApartmentAfterContent from "./ApartmentAfterContent";
import { getProjectLink } from "../../Constants/projectMapping";

// --- API Endpoints ---
const NEW_API_URL =
  "https://mithahomes.com/backend/api/mainprojectprocess/maincard";
const IMG_BASE_URL = "https://mithahomes.com/backend";

const COLORS = {
  primaryOrange: "#e46324",
  primaryGreen: "#5ec250",
  primaryDarkBlue: "#016563",
  textOrange: "#e46d3e",
  cardBg: "#ffffff",
  borderColor: "#e0e0e0",
  lightGray: "#f0f0f0",
  darkGray: "#333",
  mediumDarkGray: "#555",
  newTagRed: "#ff4444",
  disabledGray: "#cccccc",
};

const RANKED_PROJECTS = [
  "Mitha Haritham",
  "Mitha Vriksham",
  "Mitha Premam",
  "Mitha Maanasa",
  "Mitha Twins",
  "Mitha Brindhavan",
];

const COMPLETED_RANKED_PROJECTS = RANKED_PROJECTS.map((p) => p.toLowerCase());

const NEW_PROJECT_IDS = [];

const styles = {
  mainContainerStyle: {
    fontFamily: '"Plus Jakarta Sans", system-ui, -apple-system, sans-serif',
    backgroundColor: COLORS.lightGray,
    padding: "20px 15px",
    minHeight: "100vh",
  },
  gridStyle: {
    display: "grid",
    gap: "25px",
    maxWidth: "1200px",
    margin: "0 auto",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
  },
  cardContainerStyle: {
    position: "relative",
    backgroundColor: COLORS.cardBg,
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
    overflow: "hidden",
    transition: "transform 0.3s, box-shadow 0.3s",
    display: "flex",
    flexDirection: "column",
    borderRadius: "8px",
    height: "100%",
  },
  titleStyle: {
    color: "#333",
    fontSize: "18px",
    margin: "5px 15px 0",
    fontWeight: 700,
    textAlign: "center",
    paddingTop: "20px",
  },
  locationStyle: {
    fontSize: "0.95rem",
    fontWeight: 400,
    padding: "5px",
    color: "#777",
    textAlign: "center",
    margin: "0 15px 10px",
  },
  imageContainerStyle: {
    position: "relative",
    width: "100%",
    backgroundColor: "#fff",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  promoImageStyle: {
    width: "100%",
    height: "auto",
    maxHeight: "500px",
    objectFit: "contain",
    display: "block",
    transition: "transform 0.5s ease",
  },
  detailsSectionStyle: {
    padding: "15px",
    borderBottom: `1px solid ${COLORS.borderColor}`,
    flexGrow: 1,
  },
  detailsTitleStyle: {
    fontSize: "1.1em",
    margin: "0 0 12px",
    fontWeight: 700,
    color: COLORS.darkGray,
  },
  detailItemStyle: {
    display: "flex",
    alignItems: "center",
    marginBottom: "8px",
    gap: "10px",
  },
  detailPStyle: {
    margin: 0,
    fontSize: "14px",
    color: COLORS.mediumDarkGray,
  },
  detailIconStyle: {
    color: "#af9c78",
    fontSize: "22px",
    display: "flex",
  },
  actionsSectionStyle: {
    display: "flex",
    justifyContent: "space-between",
    padding: "12px",
    gap: "8px",
    flexWrap: "wrap",
  },
  btnBaseStyle: {
    flex: "1 1 0px",
    minWidth: "80px",
    display: "flex",
    borderRadius: "5px",
    alignItems: "center",
    justifyContent: "center",
    padding: "10px 5px",
    textDecoration: "none",
    color: "white",
    fontWeight: 500,
    fontSize: "0.75rem",
    transition: "opacity 0.2s, background-color 0.2s",
    cursor: "pointer",
    border: "none",
    outline: "none",
  },
  disabledBtnStyle: {
    backgroundColor: COLORS.disabledGray,
    cursor: "not-allowed",
    opacity: 0.6,
  },
  viewMoreBtnStyle: { backgroundColor: COLORS.primaryOrange },
  brochureBtnStyle: { backgroundColor: COLORS.primaryGreen },
  getDirectionBtnStyle: { backgroundColor: COLORS.primaryDarkBlue },
  btnIconStyle: { marginRight: "10px" },
  newTagStyle: {
    position: "absolute",
    top: "10px",
    right: "10px",
    backgroundColor: COLORS.newTagRed,
    color: "white",
    padding: "4px 10px",
    borderRadius: "4px",
    fontSize: "12px",
    fontWeight: "bold",
    zIndex: 10,
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
};

const CardWithShare = ({ project }) => {
  const [isHovered, setIsHovered] = useState(false);
  const projectUrl = getProjectLink(project.pid);
  const isNew = NEW_PROJECT_IDS.includes(Number(project.pid));

  const hasBrochure = project.brochure && project.brochure.trim() !== "";
  const hasDirection =
    project.get_direction && project.get_direction.trim() !== "";

  const handleCardClick = (e) => {
    e.preventDefault();
    window.open(projectUrl);
  };

  const handleViewMoreClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.location.href = projectUrl;
  };

  const handleContactClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.location.href = "/contact";
  };

  return (
    <div
      style={{ height: "100%" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        style={{
          ...styles.cardContainerStyle,
          transform: isHovered ? "translateY(-5px)" : "none",
          boxShadow: isHovered
            ? "0 8px 20px rgba(0, 0, 0, 0.15)"
            : "0 4px 12px rgba(0, 0, 0, 0.1)",
        }}
        onClick={handleCardClick}
      >
        {isNew && (
          <div style={styles.newTagStyle}>
            <FaFire /> NEW
          </div>
        )}

        <div style={{ textAlign: "center" }}>
          <h4
            style={{
              ...styles.titleStyle,
              color: isHovered ? COLORS.textOrange : "#333",
            }}
          >
            {project.project_name || "Project Name"}
          </h4>
          <h6
            style={{
              ...styles.locationStyle,
              color: isHovered ? COLORS.textOrange : "#777",
            }}
          >
            {project.location || "Location"}
          </h6>
        </div>

        <div style={styles.imageContainerStyle}>
          <img
            src={IMG_BASE_URL + (project.banner_image || "")}
            alt={project.project_name}
            style={{
              ...styles.promoImageStyle,
              transform: isHovered ? "scale(1.02)" : "scale(1)",
            }}
            onError={(e) => {
              e.target.src =
                "https://via.placeholder.com/400x250?text=Project+Image";
            }}
            loading="lazy"
          />
        </div>

        <div style={styles.detailsSectionStyle}>
          <h2 style={styles.detailsTitleStyle}>{project.project_name}</h2>
          <div style={styles.detailItemStyle}>
            <span style={styles.detailIconStyle}><GrLocation /></span>
            <p style={styles.detailPStyle}><strong>{project.location}</strong></p>
          </div>
          <div style={styles.detailItemStyle}>
            <span style={styles.detailIconStyle}><PiBedLight /></span>
            <p style={styles.detailPStyle}><strong>{project.project_caption}</strong></p>
          </div>
          <div style={styles.detailItemStyle}>
            <span style={styles.detailIconStyle}><FaProcedures /></span>
            <p style={styles.detailPStyle}><strong>{project.category_id}</strong></p>
          </div>
        </div>

        <div style={styles.actionsSectionStyle}>
          <button
            style={{ ...styles.btnBaseStyle, ...styles.viewMoreBtnStyle }}
            onClick={handleViewMoreClick}
          >
            <span style={styles.btnIconStyle}><FaHandPointUp /></span>
            View More
          </button>

          {hasBrochure ? (
            <a
              href={project.brochure}
              style={{ ...styles.btnBaseStyle, ...styles.brochureBtnStyle }}
              onClick={(e) => e.stopPropagation()}
            >
              <span style={styles.btnIconStyle}><FaFilePdf /></span>
              Brochure
            </a>
          ) : (
            <button
              style={{ ...styles.btnBaseStyle, ...styles.brochureBtnStyle }}
              onClick={handleContactClick}
            >
              <span style={styles.btnIconStyle}><MdOutlinePhone /></span>
              Contact
            </button>
          )}

          {hasDirection ? (
            <a
              href={project.get_direction}
              style={{ ...styles.btnBaseStyle, ...styles.getDirectionBtnStyle }}
              onClick={(e) => e.stopPropagation()}
            >
              <span style={styles.btnIconStyle}><FaArrowRight /></span>
              Direction
            </a>
          ) : (
            <button
              style={{ ...styles.btnBaseStyle, ...styles.disabledBtnStyle }}
              disabled
            >
              <span style={styles.btnIconStyle}><FaArrowRight /></span>
              Direction
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const ApartmentProjectSection = () => {
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAllProjects = async () => {
      try {
        const response = await fetch(NEW_API_URL);
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);
        const result = await response.json();

        if (result.success && Array.isArray(result.data)) {
          const processedProjects = result.data.filter((project) => {
            const projectNameLower = (project.project_name || "").toLowerCase();
            const isRankedProject = COMPLETED_RANKED_PROJECTS.some((r) =>
              projectNameLower.includes(r.toLowerCase())
            );
            return isRankedProject
              ? project.category_id === "Completed Projects"
              : true;
          });
          setProjects(processedProjects);
        } else {
          setError("Invalid data format.");
        }
      } catch (e) {
        setError(`Failed to fetch projects: ${e.message}`);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAllProjects();
  }, []);

  return (
    <div>
      <ApartmentBeforeContent />
      <div style={styles.mainContainerStyle}>
        <style>
          {`
            @media (min-width: 768px) { .apt-project-grid { grid-template-columns: repeat(2, 1fr) !important; } }
            @media (min-width: 1024px) { .apt-project-grid { grid-template-columns: repeat(3, 1fr) !important; } }
          `}
        </style>

        {isLoading ? (
          <div style={{ textAlign: "center", padding: "50px" }}>Loading...</div>
        ) : error ? (
          <div style={{ textAlign: "center", padding: "50px", color: "red" }}>
            {error}
          </div>
        ) : (
          <div className="apt-project-grid" style={styles.gridStyle}>
            {projects.map((project, index) => (
              <CardWithShare key={project.pid || index} project={project} />
            ))}
          </div>
        )}
      </div>
      <ApartmentAfterContent />
    </div>
  );
};

export default ApartmentProjectSection;
